<template>
  <div>
    <RSnackbar
      v-if="snackbarVisible"
      :backgroundColor="snackbarColor"
      :message="snackbarText"
      :timeout="3000"
      @close="snackbarVisible = false"
    >
    </RSnackbar>
    <!-- #region NAVBAR   -->
    <nav class="bg-red-700">
      <ul class="nav-wrapper">
        <li class="icon">
          <RouterLink to="/">
            <span class="tooltip">Home</span>
            <span><i class="fa-solid fa-house text-white ml-3"> </i></span
          ></RouterLink>
        </li>

        <li>
          <a href="#">
            <div class="ml-3 text-base lg:text-2xl text-white">
              Security Groups
            </div>
          </a>
        </li>

        <li class="icon" @click="getData">
          <a href="#">
            <span class="tooltip">Refresh</span>
            <span><i class="fa-solid fa-rotate"></i></span>
          </a>
        </li>

        <li
          v-if="user.securityGroup.keys.includes(602)"
          class="icon"
          @click="handleNavigateInitSecurityGroup"
        >
          <a href="#">
            <span class="tooltip">Add Group</span>
            <span><i class="fa-solid fa-plus"></i></span>
          </a>
        </li>

        <li>
          <RouterLink to="/"></RouterLink>
        </li>
      </ul>
    </nav>

    <!-- LOADING -->

    <r-spinner v-if="loading"> </r-spinner>

    <div v-else class="px-5 py-2">
      <div
        class="flex justify-between border-b border-gray-500 px-3 py-1 cursor-pointer"
        v-for="group in securityGroups"
        :key="group._id"
        @click="handleNavigateToSecurityGroup(group)"
      >
        <div>{{ group.name }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../services/api";
import authentication from "../services/authentication";
import { mixin } from "../mixins/mixin";
import RSnackbar from "../components/RSnackbar.vue";
import RSpinner from "../components/RSpinner.vue";
import RModal from "../components/RModal.vue";

export default {
  name: "securityGroups",
  mixins: [mixin],
  components: { RSnackbar, RSpinner, RModal },
  data() {
    return {
      loading: false,

      inactivityTimer: null,

      securityGroups: [],

      //-------------------------Snackbar
      snackbarVisible: false,
      snackbarText: "",
      snackbarColor: "white",
      snackbarTimeout: 5000,
    };
  },

  created() {
    (async () => {
      this.getData();
    })();
  },

  mounted() {
    this.startInactivityTimer();
    document.addEventListener("click", this.resetInactivityTimer);
  },
  unmounted() {
    this.clearInactivityTimer();
    document.removeEventListener("click", this.resetInactivityTimer);
  },

  computed: {
    auth() {
      return this.$store.state.auth;
    },
    user() {
      return this.$store.state.user;
    },

    company() {
      return this.$store.state.company;
    },
  },

  methods: {
    startInactivityTimer() {
      this.clearInactivityTimer();
      this.inactivityTimer = setTimeout(() => {
        this.handleAutoLogout();
      }, this.timeoutDuration);
    },

    clearInactivityTimer() {
      if (this.inactivityTimer) {
        clearTimeout(this.inactivityTimer);
      }
    },

    resetInactivityTimer() {
      this.startInactivityTimer();
    },

    async handleAutoLogout() {
      try {
        //---------------------------------------Refresh Token
        this.loading = true;
        const accessToken = await authentication.getAccessToken();
        if (!accessToken) {
          this.loading = false;
          return;
        }

        await api.get("/auth/logout", {
          headers: {
            Authorization: "Bearer " + accessToken,
          },
        });

        this.loading = false;

        this.$store.commit("updateAuth", null);
        this.$store.commit("updateUser", null);
        clearTimeout(this.inactivityTimer);
        this.$router.push({ name: "home" });
      } catch (error) {
        this.loading = false;
        this.snackbarColor = "var(--red)";
        this.snackbarVisible = true;
        this.snackbarText = `Error during auto-logout: ${error}`;

        setTimeout(() => {
          this.$store.commit("updateAuth", null);
          this.$store.commit("updateUser", null);
          clearTimeout(this.inactivityTimer);
        }, 3000);
      }
    },

    handleNavigateToSecurityGroup(group) {
      if (!this.user.securityGroup.keys.includes(601)) {
        this.snackbarColor = "var(--red)";
        this.snackbarText =
          "Not authorized. Please contact your administrator.";
        this.snackbarVisible = true;
        return;
      }

      const keys = this.company.keys.map((item) => ({
        ...item,
        active: group.keys.includes(item.code),
      }));

      const companies = this.company.companies.map((item) => ({
        name: item,
        active: group.companies.includes(item),
      }));
      const jobTitles = this.company.jobTitles.map((item) => ({
        name: item,
        active: group.jobTitles.includes(item),
      }));
      const flightAssignmentCargoRoles =
        this.company.flightAssignmentCargoRoles.map((item) => ({
          name: item,
          active: group.flightAssignmentCargoRoles.includes(item),
        }));
      const flightAssignmentPassengerRoles =
        this.company.flightAssignmentPassengerRoles.map((item) => ({
          name: item,
          active: group.flightAssignmentPassengerRoles.includes(item),
        }));
      const groundAssignmentCargoRoles =
        this.company.groundAssignmentCargoRoles.map((item) => ({
          name: item,
          active: group.groundAssignmentCargoRoles.includes(item),
        }));
      const groundAssignmentPassengerRoles =
        this.company.groundAssignmentPassengerRoles.map((item) => ({
          name: item,
          active: group.groundAssignmentPassengerRoles.includes(item),
        }));

      const securityGroup = {
        _id: group._id,
        name: group.name,
        keys,
        jobTitles,
        companies,
        flightAssignmentCargoRoles,
        flightAssignmentPassengerRoles,
        groundAssignmentCargoRoles,
        groundAssignmentPassengerRoles,
      };

      this.$store.commit("updateSecurityGroup", securityGroup);
      this.$router.push({ name: "securityGroup" });
    },

    handleNavigateInitSecurityGroup() {
      if (!this.user.securityGroup.keys.includes(602)) {
        this.snackbarColor = "var(--red)";
        this.snackbarText =
          "Not authorized. Please contact your administrator.";
        this.snackbarVisible = true;
        return;
      }

      const keys = this.company.keys.map((item) => ({
        ...item,
        active: false,
      }));

      const companies = this.company.companies.map((item) => ({
        name: item,
        active: false,
      }));
      const jobTitles = this.company.jobTitles.map((item) => ({
        name: item,
        active: false,
      }));
      const flightAssignmentCargoRoles =
        this.company.flightAssignmentCargoRoles.map((item) => ({
          name: item,
          active: false,
        }));
      const flightAssignmentPassengerRoles =
        this.company.flightAssignmentPassengerRoles.map((item) => ({
          name: item,
          active: false,
        }));
      const groundAssignmentCargoRoles =
        this.company.groundAssignmentCargoRoles.map((item) => ({
          name: item,
          active: false,
        }));
      const groundAssignmentPassengerRoles =
        this.company.groundAssignmentPassengerRoles.map((item) => ({
          name: item,
          active: false,
        }));

      const securityGroup = {
        _id: null,
        name: "",
        keys,
        jobTitles,
        companies,
        flightAssignmentCargoRoles,
        flightAssignmentPassengerRoles,
        groundAssignmentCargoRoles,
        groundAssignmentPassengerRoles,
      };

      this.$store.commit("updateSecurityGroup", securityGroup);
      this.$router.push({ name: "securityGroup" });
    },

    handleNavigateToHome() {
      this.$router.push({ name: "home" });
    },

    async getData() {
      this.securityGroups = [];

      //---------------------------------------Refresh Token
      this.loading = true;
      const accessToken = await authentication.getAccessToken();
      if (!accessToken) {
        this.loading = false;
        this.$router.push({ name: "home" });
        return;
      }

      try {
        const res = await api.get("/security-groups", {
          headers: {
            Authorization: "Bearer " + accessToken,
          },
        });

        this.loading = false;

        if (res.status !== 200) {
          this.snackbarColor = "var(--red)";
          this.snackbarText = error.message.message
            ? error.message.message
            : error.message;
          this.snackbarVisible = true;
          return;
        }

        const securityGroups = res.data.securityGroups;

        this.$store.commit("updateSecurityGroups", securityGroups);

        this.securityGroups = securityGroups;
      } catch (error) {
        this.loading = false;
        this.snackbarColor = "var(--red)";
        this.snackbarText = `Error "${error}`;
        this.snackbarVisible = true;
      }
    },
  },
};
</script>
